import { useState } from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import PageLoading from './components/pageLoading';
import i18n from './configs/i18next';
import { PathLogout } from './context/path-logout';
import { ProtectedRoute } from './context/protected-route';
import AppLayout from './layout/app-layout';
import { WelcomeLayout } from './layout/welcome-layout';
import Providers from './providers';
import informationService from './services/rest/information';
import BannerAdd from './views/banners/banner-add';
import BannerEdit from './views/banners/banner-edit';
import Banners from './views/banners/banners';
import Bbbps from './views/bbps';
import Blogs from './views/blog';
import BlogAdd from './views/blog/blog-add';
import BlogEdit from './views/blog/blog-edit';
import BrandsAdd from './views/brands/brands-add';
import BrandsEdit from './views/brands/brands-edit';
import Cashback from './views/cashback';
import CategoryAdd from './views/categories/category-add';
import CategoryEdit from './views/categories/category-edit';
import Chat from './views/chat/chat';
import Coupon from './views/coupons/Coupon';
import CouponAdd from './views/coupons/CouponAdd';
import Currencies from './views/currencies/currencies';
import CurrencyAdd from './views/currencies/currencies-add';
import CurrencyEdit from './views/currencies/currency-edit';
import Dashboard from './views/dashboard';
import Delivery from './views/delivery/delivery';
import DeliveryAdd from './views/delivery/delivery-add';
import DeliveryList from './views/delivery/delivery-list';
import Deliveryman from './views/delivery/deliveryman';
import DeliverymanOrders from './views/deliveryman-orders/order';
import DeliverymanOrderDetails from './views/deliveryman-orders/order-details';
import FaqAdd from './views/faq/faq-add';
import FaqEdit from './views/faq/faq-edit';
import Gallery from './views/gallery';
import GalleryLanguages from './views/gallery/gallery-languages';
import GiftCard from './views/giftcard/giftcard';
import GiftCardCategories from './views/giftcard/giftcard-categories';
import HomepageProducts from './views/giftcard/homepage-products';
import PaymentModes from './views/giftcard/payment-modes';
import GlobalSettings from './views/global-settings/global-settings';
import {
  default as LanguageAdd,
  default as LanguageEdit,
} from './views/languages/language-add';
import Login from './views/login/login';
import MyShop from './views/my-shop';
import MyShopEdit from './views/my-shop/edit';
import NotFound from './views/not-found';
import Notification from './views/notification';
import NotificationAdd from './views/notification/notification-add';
import NotificationEdit from './views/notification/notification-edit';
import Order from './views/order/order';
import OrderDetails from './views/order/order-details';
import OrderEdit from './views/order/order-edit';
import OrdersAdd from './views/order/orders-add';
import OtpScreen from './views/otp';
import PayoutRequests from './views/payout-requests';
import PosSystem from './views/pos-system/pos-system';
import ProductsEdit from './views/products/product-edit';
import Products from './views/products/products';
import ProductsAdd from './views/products/products-add';
import Reviews from './views/reviews';
import OrderReviews from './views/reviews/orderReviews';
import ProductReviews from './views/reviews/productReviews';
import SellerBrands from './views/seller-views/brands';
import SellerCategories from './views/seller-views/categories';
import SellerDelivery from './views/seller-views/delivery/delivery';
import SellerDeliveryAdd from './views/seller-views/delivery/delivery-add';
import SellerDeliverymans from './views/seller-views/delivery/deliverymans';
import SellerDiscounts from './views/seller-views/discounts';
import DiscountAdd from './views/seller-views/discounts/discount-add';
import DiscountEdit from './views/seller-views/discounts/discount-edit';
import SellerInvites from './views/seller-views/invites';
import SellerLooks from './views/seller-views/looks';
import LookAdd from './views/seller-views/looks/look-add';
import LookEdit from './views/seller-views/looks/look-edit';
import SellerOrder from './views/seller-views/order/order';
import SellerOrderDetails from './views/seller-views/order/order-details';
import SellerPayouts from './views/seller-views/payouts';
import SellerPosSystem from './views/seller-views/pos-system/pos-system';
import SellerProductEdit from './views/seller-views/products/product-edit';
import SellerProductImport from './views/seller-views/products/product-import';
import SellerProducts from './views/seller-views/products/products';
import SellerProductAdd from './views/seller-views/products/products-add';
import SellerSubscriptions from './views/seller-views/subscriptions';
import ShopUsers from './views/seller-views/user/shop-users';
import FirebaseConfig from './views/settings/firebaseConfig';
import Settings from './views/settings/settings';
import Shops from './views/shops/shops';
import ShopsAdd from './views/shops/shops-add';
import Subscriptions from './views/subscriptions/subscriptions';
import SubscriptionsEdit from './views/subscriptions/subscriptions-edit';
import Tickets from './views/tickets';
import TicketAdd from './views/tickets/ticketAdd';
import Transactions from './views/transactions';
import TransactionDetails from './views/transactions/transaction-details';
import UnitAdd from './views/units/unit-add';
import UnitEdit from './views/units/unit-edit';
import User from './views/user/user';
import UserAdd from './views/user/user-add';
import UserEdit from './views/user/user-edit';
import UserDetails from './views/user/user-details';
import Wallets from './views/wallet';
import Welcome from './views/welcome/welcome';

const App = () => {
  const [loading, setLoading] = useState(false);

  function fetchTranslations() {
    const params = { lang: i18n.language };
    setLoading(true);
    informationService
      .translations(params)
      .then(({ data }) =>
        i18n.addResourceBundle(i18n.language, 'translation', data),
      )
      .finally(() => setLoading(false));
  }

  // useEffect(() => {
  //   fetchTranslations();
  // }, []);

  return (
    <Providers>
      <Router>
        <Routes>
          <Route
            path='/login'
            element={
              <PathLogout>
                <Login />
              </PathLogout>
            }
          />
          <Route
            path='/otp'
            element={
              <PathLogout>
                <OtpScreen />
              </PathLogout>
            }
          />

          <Route
            path='/welcome'
            element={
              <WelcomeLayout>
                <Welcome />
              </WelcomeLayout>
            }
          />
          <Route
            path='/installation'
            element={
              <WelcomeLayout>
                <GlobalSettings />
              </WelcomeLayout>
            }
          />

          <Route
            path=''
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            {/*app  page*/}

            <Route path='/' element={<Navigate to='dashboard' />} />
            <Route path='dashboard' element={<Dashboard />} />
            <Route path='shops' element={<Shops />} />
            <Route path='pos-system' element={<PosSystem />} />
            <Route path='currencies' element={<Currencies />} />
            <Route path='gallery' element={<Gallery />} />
            <Route path='subscriptions' element={<Subscriptions />} />
            <Route path='orders' element={<Order />} />
            <Route path='banners' element={<Banners />} />
            <Route path='notifications' element={<Notification />} />
            <Route path='chat' element={<Chat />} />
            <Route path='coupons' element={<Coupon />} />
            <Route path='my-shop' element={<MyShop />} />
            <Route path='tickets' element={<Tickets />} />
            <Route path='blogs' element={<Blogs />} />
            <Route path='transactions' element={<Transactions />} />
            <Route path='transactions/:id' element={<TransactionDetails />} />
            <Route path='wallets' element={<Wallets />} />
            <Route path='payout-requests' element={<PayoutRequests />} />
            <Route path='payment-modes' element={<PaymentModes />} />

            <Route path='reviews' element={<Reviews />} />
            <Route path='reviews/product' element={<ProductReviews />} />
            <Route path='reviews/order' element={<OrderReviews />} />

            {/* settings */}
            <Route path='settings' element={<Settings />} />
            <Route path='settings/firebase' element={<FirebaseConfig />} />

            {/* giftcard */}
            <Route path='giftcard' element={<GiftCard />} />
            <Route path='giftcard/products' element={<Products />} />

            <Route
              path='giftcard/giftcard-categories'
              element={<GiftCardCategories />}
            />
            <Route
              path='giftcard/homepage-products'
              element={<HomepageProducts />}
            />

            <Route path='bbps' element={<Bbbps />} />

            <Route path='seller/categories' element={<SellerCategories />} />
            <Route path='seller/brands' element={<SellerBrands />} />
            <Route path='seller/products' element={<SellerProducts />} />
            <Route path='seller/invites' element={<SellerInvites />} />
            <Route path='seller/discounts' element={<SellerDiscounts />} />
            <Route path='seller/product/add' element={<SellerProductAdd />} />
            <Route
              path='seller/product/:uuid'
              element={<SellerProductEdit />}
            />
            <Route
              path='seller/product/import'
              element={<SellerProductImport />}
            />
            <Route path='seller/delivery/list' element={<SellerDelivery />} />
            <Route
              path='seller/deliveries/add'
              element={<SellerDeliveryAdd />}
            />
            <Route path='seller/delivery/:id' element={<SellerDeliveryAdd />} />
            <Route
              path='seller/delivery/deliveryman'
              element={<SellerDeliverymans />}
            />
            <Route path='seller/pos-system' element={<SellerPosSystem />} />
            <Route path='seller/orders' element={<SellerOrder />} />
            <Route
              path='seller/order/details/:id'
              element={<SellerOrderDetails />}
            />
            {/* <Route path='seller/clients' element={<SellerClients />} /> */}
            <Route path='seller/shop-users' element={<ShopUsers />} />
            {/* <Route path='seller/user/add' element={<SellerUserAdd />} /> */}
            <Route path='seller/payouts' element={<SellerPayouts />} />
            <Route
              path='seller/subscriptions'
              element={<SellerSubscriptions />}
            />
            <Route path='seller/looks' element={<SellerLooks />} />
            <Route path='seller/looks/add' element={<LookAdd />} />
            <Route path='seller/looks/:id' element={<LookEdit />} />

            {/* delivery */}
            <Route path='delivery' element={<DeliveryList />} />
            <Route path='delivery/list' element={<Delivery />} />
            <Route path='delivery/deliveryman' element={<Deliveryman />} />

            <Route path='deliveryman/orders' element={<DeliverymanOrders />} />
            <Route
              path='deliveryman/order/details/:id'
              element={<DeliverymanOrderDetails />}
            />

            <Route path='cashback' element={<Cashback />} />

            {/* user */}
            <Route path='users' element={<User />} />
            <Route path='user/:uuid/details' element={<UserDetails />} />
            {/* <Route path='users/user' element={<User />} />
            <Route path='users/admin' element={<Admin />} />
            <Route path='users/role' element={<RoleList />} /> */}

            {/*app add for page*/}
            <Route path='shop/add' element={<ShopsAdd />} />
            <Route path='brand/add' element={<BrandsAdd />} />
            <Route path='category/add' element={<CategoryAdd />} />
            <Route path='product/add' element={<ProductsAdd />} />
            <Route path='language/add' element={<LanguageAdd />} />
            <Route path='currency/add' element={<CurrencyAdd />} />
            <Route path='order/add' element={<OrdersAdd />} />
            <Route path='deliveries/add' element={<DeliveryAdd />} />
            <Route path='user/add' element={<UserAdd />} />
            <Route path='banner/add' element={<BannerAdd />} />
            <Route path='notification/add' element={<NotificationAdd />} />
            <Route path='coupon/add' element={<CouponAdd />} />
            <Route path='ticket/add' element={<TicketAdd />} />
            <Route path='unit/add' element={<UnitAdd />} />
            <Route path='discount/add' element={<DiscountAdd />} />
            <Route path='blog/add' element={<BlogAdd />} />
            <Route path='faq/add' element={<FaqAdd />} />

            {/*app edit for page*/}
            <Route path='language/:id' element={<LanguageEdit />} />
            <Route path='category/:uuid' element={<CategoryEdit />} />
            <Route path='currency/:id' element={<CurrencyEdit />} />
            <Route path='brand/:id' element={<BrandsEdit />} />
            <Route path='shop/:uuid' element={<ShopsAdd />} />
            <Route path='product/:uuid' element={<ProductsEdit />} />
            <Route path='subscriptions/edit' element={<SubscriptionsEdit />} />
            <Route path='order/details/:id' element={<OrderDetails />} />
            <Route path='delivery/:id' element={<DeliveryAdd />} />
            <Route path='order/:id' element={<OrderEdit />} />
            <Route path='user/:uuid' element={<UserEdit />} />
            <Route path='banner/:id' element={<BannerEdit />} />
            <Route path='notification/:uuid' element={<NotificationEdit />} />
            <Route path='coupon/:id' element={<CouponAdd />} />
            <Route path='unit/:id' element={<UnitEdit />} />
            <Route path='my-shop/edit' element={<MyShopEdit />} />
            <Route path='discount/:id' element={<DiscountEdit />} />
            <Route path='blog/:uuid' element={<BlogEdit />} />
            <Route path='faq/:uuid' element={<FaqEdit />} />
            <Route path='gallery/:type' element={<GalleryLanguages />} />
          </Route>

          <Route path='*' element={<NotFound />} />
        </Routes>
        <ToastContainer
          className='antd-toast'
          position='top-right'
          autoClose={3000}
          hideProgressBar
          closeOnClick
          pauseOnHover
          draggable
        />
        {loading && <PageLoading />}
      </Router>
    </Providers>
  );
};
export default App;
